import React, {forwardRef, useImperativeHandle, useState} from 'react';
import PropTypes from 'prop-types';
import {Checkbox, Modal, Radio} from 'antd';
import {useAsyncRetry} from 'react-use';
import _ from 'lodash';
import {isPromise} from '@/util/PromiseUtils';
import {getAllApps} from '@/api/apps';

import styles from './styles.module.less';

/**
 * 在应用列表中选择。
 */
const AppSelector = forwardRef(({title = '选择应用', multi = true, onSelect}, ref) => {
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [indeterminate, setIndeterminate] = React.useState(true);
    const [checkAll, setCheckAll] = React.useState(false);
    const [appOptions, setAppOptions] = useState([]);

    // checkbox: 以应用 id 为 value，name 为 label 的数组
    const [checkedList, setCheckedList] = useState([]);

    // radio: 单个的 id
    const [checkedValue, setCheckedValue] = useState(null);

    const {value: apps} = useAsyncRetry(() => {
        return getAllApps().then(({data}) => {
            if (_.isEmpty(data)) {
                setAppOptions([]);
            } else {
                const options = data.map(({id, name}) => ({label: name, value: id}));
                setAppOptions(options);
            }
            return data;
        });
    }, []);

    // console.log('apps', apps);

    /**
     * multi 为 true 时，是多选框，给定参数也是数组，为 false 时是单选，参数是单个值
     * @param initValues 初始值
     */
    function showModal(initValues) {
        setVisible(true);

        if (multi) {
            changeCheckboxState(initValues, appOptions);
        } else {
            setCheckedValue(initValues);
        }
    }

    function closeModal() {
        setVisible(false);
        // setAppOptions([]);
        // setCheckedList([]);
        // setCheckAll(false);
        // setIndeterminate(true);
        setLoading(false);
    }

    useImperativeHandle(ref, () => ({
        showModal,
        closeModal,
    }));

    function changeCheckboxState(list = [], options = []) {
        console.log('changeCheckboxState', list, options);
        setCheckedList(list);
        setIndeterminate(!!list.length && list.length < options.length);
        setCheckAll(list.length === options.length);
    }

    function onChange(e) {
        let value;
        if (multi) {
            value = e || [];
            changeCheckboxState(value, appOptions);
        } else {
            value = e.target.value;
            setCheckedValue(value);
        }
        console.log('onChange', value);
    }

    function onCheckAllChange(e) {
        setCheckedList(e.target.checked ? appOptions.map(({value}) => value) : []);
        setIndeterminate(false);
        setCheckAll(e.target.checked);
    }

    function onOk() {
        setLoading(true);

        const result = onSelect(multi ? checkedList : checkedValue);
        if (isPromise(result)) {
            result.then(data => {
                // console.log(data);
                if (data === true) {
                    closeModal();
                } else {
                    setLoading(false);
                }
            });
        } else {
            if (result) {
                closeModal();
            } else {
                setLoading(false);
            }
        }
    }

    const modalTitle = (
        <div className={styles.modalTitle}>
            <div>{title}</div>
            {multi && (
                <Checkbox
                    indeterminate={indeterminate}
                    onChange={onCheckAllChange}
                    checked={checkAll}
                    style={{marginRight: 48}}
                >
                    全选
                </Checkbox>
            )}
        </div>
    );

    const Comp = multi ? Checkbox.Group : Radio.Group;

    return (
        <Modal
            destroyOnClose
            title={modalTitle}
            visible={visible}
            onCancel={closeModal}
            onOk={onOk}
            okButtonProps={{loading}}
        >
            <Comp
                className={styles.group}
                options={appOptions}
                value={multi ? checkedList : checkedValue}
                onChange={onChange}
            />
        </Modal>
    );
});

AppSelector.displayName = 'AppSelector';

AppSelector.propTypes = {
    /**
     * modal 标题
     */
    title: PropTypes.node,

    /**
     * 是否多选或单选，默认多选
     */
    multi: PropTypes.bool,

    /**
     * multi 为 true 时，返回值是 id 数组，为 false 时，返回值是单个 id
     */
    onSelect: PropTypes.func.isRequired,
};

export default AppSelector;
